import { useState } from 'react';

import { useCohortCancellation, useCohortRetention } from '@/lib/api/adminHooks';
import type { CohortCycleFilter } from '@/types/admin';

import { CohortHeatmap, type HeatmapMetric } from './CohortHeatmap';
import { SyncStatusBar } from './SyncStatusBar';

const DEFAULT_CAP: Record<HeatmapMetric, number> = {
  retention: 6,
  cancellation: 4,
};

const CAP_OPTIONS: Record<HeatmapMetric, number[]> = {
  retention: [3, 4, 5, 6, 8, 12],
  cancellation: [3, 4, 6, 8],
};

export function CohortRetentionScreen() {
  const [metric, setMetric] = useState<HeatmapMetric>('retention');
  const [cycle, setCycle] = useState<CohortCycleFilter>('monthly');
  const [days, setDays] = useState(180);
  const [cap, setCap] = useState(DEFAULT_CAP.retention);

  const retention = useCohortRetention({
    cycle,
    days,
    cap,
    groupBy: 'label',
    enabled: metric === 'retention',
  });
  const cancellation = useCohortCancellation({
    cycle,
    days,
    cap,
    groupBy: 'label',
    enabled: metric === 'cancellation',
  });
  const { data, isLoading, isError, error } =
    metric === 'retention' ? retention : cancellation;

  const handleMetricChange = (next: HeatmapMetric) => {
    setMetric(next);
    setCap(DEFAULT_CAP[next]);
  };

  const isCancellation = metric === 'cancellation';
  const capUnitLabel = isCancellation ? 'Weeks' : 'Months';
  const capPrefix = isCancellation ? 'W' : 'M';

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Cohorts</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          {isCancellation
            ? 'Weekly signup cohorts × W_N early-cancellation rates, per product. W_N counts subs that clicked cancel during week N after signup (days N·7 to (N+1)·7) — surfaces where within the first month patients bail before their first renewal charge. Per-week, not cumulative.'
            : 'Weekly signup cohorts × M_N paid-renewal retention, per product. M_N counts a subscription as retained only when a paid subscription_cycle order has actually been captured — CP\u2019s internal renewed-log overstates retention by ~13pts because it increments even on failed charges.'}
        </p>
      </div>

      <SyncStatusBar />

      <div className="flex flex-wrap items-center gap-16 p-12 border border-line rounded-lg">
        <Segmented
          label="Metric"
          options={[
            { label: 'Retention', value: 'retention' },
            { label: 'Early cancellation', value: 'cancellation' },
          ]}
          value={metric}
          onChange={(v) => handleMetricChange(v as HeatmapMetric)}
        />
        <Segmented
          label="Cycle"
          options={[
            { label: 'Monthly', value: 'monthly' },
            { label: 'Quarterly', value: 'quarterly' },
            { label: 'All', value: 'all' },
          ]}
          value={cycle}
          onChange={(v) => setCycle(v as CohortCycleFilter)}
        />
        <label className="flex items-center gap-8 type-label-sm">
          <span className="text-ink-muted">Window</span>
          <select
            className="bg-raised border border-line rounded px-8 py-4"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
            <option value={730}>2 years</option>
          </select>
        </label>
        <label className="flex items-center gap-8 type-label-sm">
          <span className="text-ink-muted">{capUnitLabel}</span>
          <select
            className="bg-raised border border-line rounded px-8 py-4"
            value={cap}
            onChange={(e) => setCap(Number(e.target.value))}
          >
            {CAP_OPTIONS[metric].map((n) => (
              <option key={n} value={n}>
                {capPrefix}0–{capPrefix}
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isError && (
        <div className="p-16 border border-line rounded-lg bg-raised">
          <p className="type-label-md text-ink">Failed to load cohort data.</p>
          <p className="type-label-sm text-ink-muted mt-4">{String(error)}</p>
        </div>
      )}

      {isLoading && !data ? (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          Loading cohorts…
        </div>
      ) : null}

      {data && data.tables.length === 0 && (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          No subscriptions match this filter in the selected window.
        </div>
      )}

      <div className="flex flex-col gap-20">
        {data?.tables.map((table) => (
          <CohortHeatmap
            key={table.product_id}
            table={table}
            metric={metric}
            detailHref={`/admin/products/${encodeURIComponent(
              table.product_label.trim()
            )}${isCancellation ? '?metric=cancellation' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-8 type-label-sm">
      <span className="text-ink-muted">{label}</span>
      <div className="flex rounded border border-line overflow-hidden">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-12 py-6 ${
              value === o.value
                ? 'bg-ink text-ink-onAccent'
                : 'bg-raised hover:bg-sunken text-ink-muted'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
